import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
  InputLabel,
  useTheme,
} from "@mui/material";
import {
  People,
  School,
  HowToReg,
  EventAvailable,
  LocationCity,
  Refresh,
  Dashboard,
  Settings,
  LockReset,
  Menu as MenuIcon,
  ArrowBack,
  Logout,
  Search,
  GetApp,
  FilterList,
  Visibility,
  Close as CloseIcon,
  Person,
  Assessment,
  TrendingUp,
  CheckCircle,
  Male,
  Female,
  ChildCare,
  Group,
  AdminPanelSettings,
  Domain,
  AccountTree,
  LocationOn as LocationIcon,
  Business,
  Home,
  Print,
  PictureAsPdf,
  PhotoLibrary,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import axios from "axios";
import { baseUrl, config, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useReactToPrint } from "react-to-print";

// ============================================
// Modern Dashboard Theme - Matching AdminDashboard
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching AdminDashboard
// ============================================
const DashboardHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

const FilterBar = styled(Paper)({
  padding: "16px 20px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
});

const SearchField = styled(TextField)({
  flex: 2,
  minWidth: "300px",
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
  },
});

const FilterSelect = styled(FormControl)({
  minWidth: '120px',
  backgroundColor: '#ffffff',
});

const ActionButton = styled(Button)({
  border: "1px solid #e5e7eb",
  color: "#374151",
  backgroundColor: "#ffffff",
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: "#f9fafb",
    borderColor: "#d1d5db",
  },
});

const IconActionButton = styled(IconButton)({
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "8px",
  backgroundColor: "#ffffff",
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

const StyledTableContainer = styled(TableContainer)({
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "16px",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#f8f9fa",
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: "13px",
    color: "#4b5563",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "14px",
  borderBottom: "1px solid #e5e7eb",
});

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

const ViewButton = styled(Button)({
  backgroundColor: "#243b5a",
  color: "white",
  padding: "4px 12px",
  fontSize: "12px",
  minWidth: "60px",
  "&:hover": {
    backgroundColor: "#162c47",
  },
  "& svg": {
    fontSize: "16px",
    marginRight: "4px",
  },
});

const StatsGrid = styled(Grid)({
  marginBottom: "32px",
});

const StatCard = styled(Paper)({
  padding: "20px 24px",
  height: "100%",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  '&:hover': {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
    borderColor: "#243b5a",
  },
});

const StatLabel = styled(Typography)({
  fontSize: "14px",
  color: "#6b7280",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontWeight: 500,
});

const StatValue = styled(Typography)({
  fontSize: "32px",
  fontWeight: 700,
  color: "#1f2937",
  lineHeight: 1.2,
});

// Dialog specific styled components
const DialogHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: "1px solid #e5e7eb",
});

const DialogTitleStyled = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "#243b5a",
});

const SchoolStatsGrid = styled(Grid)({
  marginBottom: "24px",
});

const SchoolStatCard = styled(Paper)({
  padding: "16px",
  textAlign: "center",
  height: "100%",
  backgroundColor: "#f8fafc",
});

const SchoolStatValue = styled(Typography)({
  fontSize: "24px",
  fontWeight: 700,
  color: "#243b5a",
  lineHeight: 1.2,
});

const SchoolStatLabel = styled(Typography)({
  fontSize: "12px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginTop: "4px",
});

// Score chip styled components
const ScoreBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const TermScore = styled(Typography)({
  fontFamily: 'monospace',
  backgroundColor: '#f3f4f6',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '12px',
});

const AvgChip = styled(Chip)({
  fontWeight: 'bold',
  minWidth: '40px',
});

// Tab Button Styled Component
const TabButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#243b5a" : "#6b7280",
  borderBottom: active ? "3px solid #243b5a" : "none",
  borderRadius: 0,
  paddingBottom: "8px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "14px",
  '&:hover': {
    backgroundColor: "transparent",
    color: "#243b5a",
  },
}));

// ============================================
// Types
// ============================================
interface LGA {
  lgaCode: string;
  lgaName: string;
  name?: string;
}

interface School {
  schoolId: string;
  schoolCode?: string;
  schoolName: string;
  lgaCode: string;
  totalStudents: number;
  address?: string;
  schoolType?: string;
}

interface Student {
  id: string | number;
  studentNumber: string;
  firstname: string;
  lastname: string;
  othername?: string;
  dateOfBirth: string;
  gender: string;
  passport?: string;
  schoolId: string | number;
  schoolName?: string;
  className?: string;
  registrationStatus?: string;
  regStatus?: string | number;
  year?: string;
  prcd?: number;
  accCode?: string;
  englishTerm1?: string;
  englishTerm2?: string;
  englishTerm3?: string;
  arithmeticTerm1?: string;
  arithmeticTerm2?: string;
  arithmeticTerm3?: string;
  generalTerm1?: string;
  generalTerm2?: string;
  generalTerm3?: string;
  religiousTerm1?: string;
  religiousTerm2?: string;
  religiousTerm3?: string;
  religiousType?: string;
  schoolType?: string;
  lgacode?: number;
  lateRegistration?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface SubjectStat {
  schoolName: string;
  regEnglish: number;
  regArithmetic: number;
  regGeneral: number;
  regCRS: number;
  regIRS: number;
  totalStudents: number;
}

// ============================================
// API Service Functions
// ============================================
const apiService = {
  // Endpoint 1: Get list of LGAs
  getLGAs: async (): Promise<LGA[]> => {
    try {
      const mockLGAs = await axios.post(`${baseUrl()}Admin2s/getLGA`, config);
      return mockLGAs.data?.data || [];
    } catch (error) {
      console.error("API Error getLGAs:", error);
      return [];
    }
  },

  // Endpoint 2: Get schools by LGA code
  getSchoolsByLGA: async (lgaCode: string, status: 'all' | 1 | 2 | 3 = 'all'): Promise<School[]> => {
    const payload: Record<string, any> = { lgaCode };
    if (status !== 'all') {
      payload.status = status;
    }
    const response = await axios.post(`${baseUrl()}Admin2s/schoolsByLGA`, payload, config);
    return response.data.data;
  },

  // Endpoint 3: Get students by school ID and LGA code
  getStudentsBySchool: async (schCode: string, lgaCode: string): Promise<any> => {
    console.log("Fetching students for:", schCode, lgaCode);
    
    try {
      const response = await axios.post(
        `${baseUrl()}Admin2s/studentsBySchool_lga`, 
        { schCode: schCode, lgaCode: lgaCode }, 
        config
      );
      
      console.log("API Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error in API call:", error);
      throw error;
    }
  },

  getSubjectStatsByLGA: async (lgaCode: string): Promise<any> => {
    const response = await axios.post(`${baseUrl()}Admin2s/getSubjectStatsByLGA`, { lgaCode }, config);
    return response.data;
  },

  // Endpoint 4: Get all students and schools by LGA for database export
  exportStudentsByLGA: async (lgaCode: string): Promise<any> => {
    const response = await axios.post(`${baseUrl()}Admin2s/exportStudentsByLGA`, { lgaCode }, config);
    return response.data;
  },

};

// ============================================
// School List Modal Component
// ============================================
interface SchoolListModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  schools: School[];
  loading: boolean;
  onViewSchool: (school: School) => void;
}

const SchoolListModal: React.FC<SchoolListModalProps> = ({
  open,
  onClose,
  title,
  schools,
  loading,
  onViewSchool
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredSchools, setFilteredSchools] = useState<School[]>(schools);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredSchools(schools);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = schools.filter(school => 
        school.schoolName.toLowerCase().includes(term) ||
        (school.schoolCode && school.schoolCode.toLowerCase().includes(term))
      );
      setFilteredSchools(filtered);
    }
  }, [searchTerm, schools]);

  const formatNumber = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '0';
    const num = Number(value);
    return isNaN(num) ? '0' : num.toLocaleString();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxHeight: "80vh",
        }
      }}
    >
      <DialogHeader>
        <Box>
          <DialogTitleStyled>{title}</DialogTitleStyled>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Total Schools: {schools.length}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      
      <DialogContent sx={{ p: 2 }}>
        {/* Search Bar */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search schools by name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2 }}>Loading schools...</Typography>
          </Box>
        ) : filteredSchools.length === 0 ? (
          <Alert severity="info">
            No schools found.
          </Alert>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e5e7eb" }}>
            <Table stickyHeader size="small">
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 50 }}>#</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>School Name</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>School ID</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>Total Students</TableCell>
                  <TableCell align="center" sx={{ minWidth: 100 }}>Action</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {filteredSchools.map((school, index) => (
                  <StyledTableRow key={school.schoolId || index}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: "#243b5a" }}>
                          <School sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {school.schoolName}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={school.schoolCode || school.schoolId || 'N/A'}
                        size="small"
                        sx={{ bgcolor: "#e3f2fd", color: "#243b5a", fontWeight: 600 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatNumber(school.totalStudents)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <ViewButton
                        size="small"
                        onClick={() => {
                          onViewSchool(school);
                          onClose();
                        }}
                        startIcon={<Visibility />}
                      >
                        View
                      </ViewButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: "#243b5a" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Student Details Modal Component - Enhanced with all fields
// ============================================
interface StudentModalProps {
  open: boolean;
  onClose: () => void;
  school: School | null;
  students: Student[];
  loading: boolean;
}

const StudentModal: React.FC<StudentModalProps> = ({ 
  open, 
  onClose, 
  school, 
  students, 
  loading 
}) => {
  // Helper function to get image source from base64
  const getImageSrc = (base64String?: string) => {
    if (!base64String) return null;
    return `data:image/jpeg;base64,${base64String}`;
  };

  // Helper function to calculate average
  const calculateAverage = (t1?: string, t2?: string, t3?: string) => {
    const scores = [parseInt(t1 || '0'), parseInt(t2 || '0'), parseInt(t3 || '0')].filter(
      (s) => !isNaN(s)
    );
    if (scores.length === 0) return "0.0";
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    return avg.toFixed(1);
  };

  // Helper function to get religious type display
  const getReligiousType = (type?: string) => {
    if (!type) return "N/A";
    return type === "1" ? "Christian" : type === "2" ? "Muslim" : "Other";
  };

  // Helper function to get school type display
  const getSchoolType = (type?: string) => {
    if (!type) return "N/A";
    return type === "public" ? "Public" : type === "private" ? "Private" : type;
  };

  // Helper function to get full name
  const getFullName = (student: Student) => {
    return `${student.firstname || ''} ${student.othername ? student.othername + ' ' : ''}${student.lastname || ''}`;
  };

  const getRegStatusValue = (student: Student): 1 | 2 | 3 => {
    const rawValue = student.regStatus ?? student.registrationStatus ?? student.lateRegistration;
    if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase();
      if (normalized === 'pos_late' || normalized === '3') return 3;
      if (normalized === 'late' || normalized === '2') return 2;
      return 1;
    }
    const numericValue = Number(rawValue);
    if (numericValue === 2) return 2;
    if (numericValue === 3) return 3;
    return 1;
  };

  const getRegStatusLabel = (status: 1 | 2 | 3) => {
    if (status === 2) return "Late";
    if (status === 3) return "Pos Late";
    return "Normal";
  };

  const [regStatusFilter, setRegStatusFilter] = useState<'all' | 1 | 2 | 3>('all');

  const regStatusCounts = useMemo(() => {
    return students.reduce((acc, student) => {
      const status = getRegStatusValue(student);
      acc[status] = (acc[status] ?? 0) + 1;
      return acc;
    }, { 1: 0, 2: 0, 3: 0 } as Record<1 | 2 | 3, number>);
  }, [students]);

  const filteredStudents = useMemo(() => {
    if (regStatusFilter === 'all') return students;
    return students.filter((student) => getRegStatusValue(student) === regStatusFilter);
  }, [students, regStatusFilter]);

  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Create a ref for the printable content
  const printRef = useRef<HTMLDivElement>(null);

  // Handle print - FIXED: Properly set up useReactToPrint
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${school?.schoolName || 'School'}_Student_Directory`,
    onPrintError: (error) => {
      console.error("Print error:", error);
      alert("There was an error printing. Please try again.");
    },
    onBeforeGetContent: () => {
      console.log("Preparing to print...");
      return Promise.resolve();
    },
  });

  // Helper function to get image data for PDF
  const getImageDataForPDF = async (student: Student): Promise<string | null> => {
    if (!student.passport) return null;
    
    // Check if passport is already a base64 string (longer than 100 chars and no slashes)
    // or if it already starts with the data: prefix
    const isBase64 = student.passport.length > 50; 
    
    if (student.passport.startsWith("data:")) {
      return student.passport;
    } 
    
    if (isBase64 && !student.passport.includes('/') && !student.passport.includes('http')) {
      return `data:image/jpeg;base64,${student.passport}`;
    }
    
    // Fallback: Fetch as URL if it's clearly a path
    //const url = `${baseUrl()}assets${student.passport}`;\
    const url = `${baseUrl()}assets${
    student.passport?.trim() 
        ? student.passport 
        : `/img/students/${student.studentNumber}.jpg`
}`;
    try {
      const response = await fetch(url, config);
      if (response.ok) {
        const blob = await response.blob();
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      }
    } catch (error) {
      console.error("Fetch fallback error:", error);
    }
    return null;
  };

  const handleDownloadStudentPDF = async () => {
    if (!school || filteredStudents.length === 0) return;
    
    setGeneratingPDF(true);
    console.log("PDF Generation Started");
    
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // Pre-load all images
      const imageMap = new Map<number, string>();
      for (let i = 0; i < filteredStudents.length; i++) {
        const student = filteredStudents[i];
        if (student.passport) {
          const imageData = await getImageDataForPDF(student);
          if (imageData) {
            imageMap.set(i, imageData);
          }
        }
      }
      
      console.log(`Loaded ${imageMap.size} images for PDF`);

      // Add Header
      doc.setFillColor(36, 59, 90);
      doc.rect(0, 0, 297, 20, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text("MINISTRY OF PRIMARY EDUCATION - DELTA STATE", 10, 13);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text(`SCHOOL: ${school?.schoolName?.toUpperCase() || "N/A"}`, 10, 30);
      doc.setFontSize(10);
      doc.text(`LGA: ${school?.lgaCode || "N/A"} | SCHOOL ID: ${school?.schoolCode || school?.schoolId || "N/A"}`, 10, 36);
      doc.text(`TOTAL STUDENTS: ${filteredStudents.length}`, 10, 42);
      doc.text(`REPORT GENERATED: ${new Date().toLocaleString()}`, 10, 48);

      const tableData = filteredStudents
        .filter(s => s !== undefined && s !== null)
        .map((s, index) => [
          index + 1,
          "", // Placeholder for Photo
          s.studentNumber || "N/A",
          getFullName(s),
          s.gender || "N/A",
          getReligiousType(s.religiousType),
          getSchoolType(s.schoolType),
          `${s.englishTerm1 || '0'}/${s.englishTerm2 || '0'}/${s.englishTerm3 || '0'} (${calculateAverage(s.englishTerm1, s.englishTerm2, s.englishTerm3)})`,
          `${s.arithmeticTerm1 || '0'}/${s.arithmeticTerm2 || '0'}/${s.arithmeticTerm3 || '0'} (${calculateAverage(s.arithmeticTerm1, s.arithmeticTerm2, s.arithmeticTerm3)})`,
          `${s.generalTerm1 || '0'}/${s.generalTerm2 || '0'}/${s.generalTerm3 || '0'} (${calculateAverage(s.generalTerm1, s.generalTerm2, s.generalTerm3)})`,
          `${s.religiousTerm1 || '0'}/${s.religiousTerm2 || '0'}/${s.religiousTerm3 || '0'} (${calculateAverage(s.religiousTerm1, s.religiousTerm2, s.religiousTerm3)})`,
          s.year || "N/A",
          s.lateRegistration === 1 ? "Late" : "Normal",
        ]);

      console.log("Rendering table...");
      autoTable(doc, {
        startY: 55,
        head: [[
          "#",
          "Photo",
          "Student #",
          "Full Name",
          "Gender",
          "Religion",
          "Type",
          "English (Avg)",
          "Arith. (Avg)",
          "Gen. (Avg)",
          "Relig. (Avg)",
          "Year",
          "Status"
        ]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [36, 59, 90], textColor: [255, 255, 255], fontSize: 8 },
        bodyStyles: { fontSize: 7, minCellHeight: 14 },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 15 }, 
          2: { cellWidth: 20 },
          3: { cellWidth: 35 },
          4: { cellWidth: 12 },
          5: { cellWidth: 12 },
          6: { cellWidth: 12 },
          11: { cellWidth: 10 },
          12: { cellWidth: 15 },
        },
        margin: { top: 55 },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const imageData = imageMap.get(data.row.index);
            
            if (imageData) {
              const x = data.cell.x + 2.5;
              const y = data.cell.y + 1;
              try {
                doc.addImage(imageData, "JPEG", x, y, 11, 12, undefined, "FAST");
              } catch (addImgErr) {
                console.error("Error adding image to PDF:", addImgErr);
                doc.setFontSize(5);
                doc.text("Err", data.cell.x + 5, data.cell.y + 7);
              }
            } else {
              doc.setFontSize(5);
              doc.setTextColor(150, 150, 150);
              doc.text("No Photo", data.cell.x + 3, data.cell.y + 7);
              doc.setTextColor(0, 0, 0);
            }
          }
        },
      });

      console.log("Saving PDF...");
      const fileName = `${school?.schoolName?.replace(/\s+/g, '_') || 'School'}_Students.pdf`;
      doc.save(fileName);
      console.log("PDF saved successfully:", fileName);
    } catch (error) {
      console.error("CRITICAL ERROR during PDF generation:", error);
      alert(`Error generating PDF: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Create a separate component for the printable content
  const PrintableContent = () => (
    <div ref={printRef} style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Print Header */}
      <div style={{ 
        textAlign: "center", 
        borderBottom: "2px solid #243b5a", 
        marginBottom: "20px",
        paddingBottom: "10px"
      }}>
        <h2 style={{ color: "#243b5a", margin: 0 }}>{school?.schoolName}</h2>
        <p style={{ margin: "5px 0" }}>Student Directory Report</p>
        <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
          LGA: {school?.lgaCode} | School ID: {school?.schoolCode || school?.schoolId}
        </p>
        <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
          Generated on: {new Date().toLocaleString()} | Total Students: {filteredStudents.length}
        </p>
      </div>

      {/* Student Table for Print */}
      <table style={{ 
        width: "100%", 
        borderCollapse: "collapse", 
        fontSize: "10px",
        border: "1px solid #ddd"
      }}>
        <thead>
          <tr style={{ backgroundColor: "#243b5a", color: "white" }}>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>#</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Student #</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Full Name</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Gender</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Religion</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>English</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Arithmetic</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>General</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Religious</th>
            <th style={{ padding: "8px", border: "1px solid #ddd", textAlign: "left" }}>Year</th>
           </tr>
        </thead>
        <tbody>
          {filteredStudents.map((student, index) => (
            <tr key={student.id || index} style={{ borderBottom: "1px solid #eee" }}>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>{index + 1}</td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>{student.studentNumber}</td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>{getFullName(student)}</td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>{student.gender || "N/A"}</td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>{getReligiousType(student.religiousType)}</td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>
                {calculateAverage(student.englishTerm1, student.englishTerm2, student.englishTerm3)}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>
                {calculateAverage(student.arithmeticTerm1, student.arithmeticTerm2, student.arithmeticTerm3)}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>
                {calculateAverage(student.generalTerm1, student.generalTerm2, student.generalTerm3)}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>
                {calculateAverage(student.religiousTerm1, student.religiousTerm2, student.religiousTerm3)}
              </td>
              <td style={{ padding: "6px 8px", border: "1px solid #ddd" }}>{student.year || "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Print Footer */}
      <div style={{ 
        marginTop: "20px", 
        paddingTop: "10px", 
        borderTop: "1px solid #ddd", 
        textAlign: "center", 
        fontSize: "9px", 
        color: "#666" 
      }}>
        <p>Ministry of Primary Education - Delta State Government</p>
        <p>This is an official document. Generated on {new Date().toLocaleString()}</p>
      </div>
    </div>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxHeight: "90vh",
        }
      }}
    >
      <DialogHeader>
        <Box>
          <DialogTitleStyled>
            {school?.schoolName || "School Details"}
          </DialogTitleStyled>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            School ID: {school?.schoolId || school?.schoolCode || "N/A"} • Total Students: {filteredStudents.length}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      
      <DialogContent sx={{ p: 2, bgcolor: "#fafafa" }}>
        {/* Printable content - hidden on screen, visible when printing */}
        <div style={{ display: 'none' }}>
          <PrintableContent />
        </div>

        {/* Screen View Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: "column" }}>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2 }}>Loading student records...</Typography>
          </Box>
        ) : students.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No students found for this school.
          </Alert>
        ) : filteredStudents.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No students match the selected registration status.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <FilterSelect size="small">
                <InputLabel id="reg-status-filter-label">Registration Status</InputLabel>
                <Select
                  labelId="reg-status-filter-label"
                  value={regStatusFilter}
                  label="Registration Status"
                  size="small"
                  onChange={(e) => setRegStatusFilter(e.target.value as 'all' | 1 | 2 | 3)}
                >
                  <MenuItem value="all">All ({students.length})</MenuItem>
                  <MenuItem value={1}>Normal ({regStatusCounts[1]})</MenuItem>
                  <MenuItem value={2}>Late ({regStatusCounts[2]})</MenuItem>
                  <MenuItem value={3}>Pos Late ({regStatusCounts[3]})</MenuItem>
                </Select>
              </FilterSelect>

              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Showing {filteredStudents.length} of {students.length} students
              </Typography>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 500, border: "1px solid #e5e7eb" }}>
              <Table stickyHeader size="small">
                <StyledTableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 50 }}>#</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Photo</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Student #</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Full Name</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Gender</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Religious Type</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>School Type</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>English (T1-T3 | Avg)</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Arithmetic (T1-T3 | Avg)</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>General (T1-T3 | Avg)</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Religious (T1-T3 | Avg)</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Year</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {filteredStudents.map((student, index) => (
                    <StyledTableRow key={student.id || index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      
                      {/* Photo */}
                      <StyledTableCell>  
                        <Avatar
                          src={`${baseUrl()}assets${student.passport}`}
                          sx={{ 
                            width: 45, 
                            height: 45,
                            border: "2px solid #243b5a",
                            bgcolor: "#7986cb"
                          }}
                        >
                          {student.firstname?.[0]}{student.lastname?.[0]}
                        </Avatar>
                      </StyledTableCell>
                      
                      {/* Student Number */}
                      <StyledTableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold", fontFamily: "monospace" }}>
                          {student.studentNumber}
                        </Typography>
                      </StyledTableCell>
                      
                      {/* Full Name */}
                      <StyledTableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getFullName(student)}
                        </Typography>
                      </StyledTableCell>
                      
                      {/* Gender */}
                      <StyledTableCell>
                        <Chip 
                          label={student.gender || "N/A"} 
                          size="small"
                          icon={student.gender?.toLowerCase() === "male" ? <Male /> : student.gender?.toLowerCase() === "female" ? <Female /> : undefined}
                          color={student.gender?.toLowerCase() === "male" ? "primary" : "secondary"}
                          variant="outlined"
                          sx={{ textTransform: "capitalize" }}
                        />
                      </StyledTableCell>
                      
                      {/* Religious Type */}
                      <StyledTableCell>
                        <Chip 
                          label={getReligiousType(student.religiousType)} 
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </StyledTableCell>
                      
                      {/* School Type */}
                      <StyledTableCell>
                        <Chip 
                          label={getSchoolType(student.schoolType)} 
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </StyledTableCell>
                      
                      {/* English Scores */}
                      <StyledTableCell>
                        <ScoreBox>
                          <TermScore>
                            {student.englishTerm1 || '0'}/{student.englishTerm2 || '0'}/{student.englishTerm3 || '0'}
                          </TermScore>
                          <AvgChip 
                            label={calculateAverage(student.englishTerm1, student.englishTerm2, student.englishTerm3)} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </ScoreBox>
                      </StyledTableCell>
                      
                      {/* Arithmetic Scores */}
                      <StyledTableCell>
                        <ScoreBox>
                          <TermScore>
                            {student.arithmeticTerm1 || '0'}/{student.arithmeticTerm2 || '0'}/{student.arithmeticTerm3 || '0'}
                          </TermScore>
                          <AvgChip 
                            label={calculateAverage(student.arithmeticTerm1, student.arithmeticTerm2, student.arithmeticTerm3)} 
                            size="small" 
                            color="success"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </ScoreBox>
                      </StyledTableCell>
                      
                      {/* General Scores */}
                      <StyledTableCell>
                        <ScoreBox>
                          <TermScore>
                            {student.generalTerm1 || '0'}/{student.generalTerm2 || '0'}/{student.generalTerm3 || '0'}
                          </TermScore>
                          <AvgChip 
                            label={calculateAverage(student.generalTerm1, student.generalTerm2, student.generalTerm3)} 
                            size="small" 
                            color="warning"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </ScoreBox>
                      </StyledTableCell>
                      
                      {/* Religious Scores */}
                      <StyledTableCell>
                        <ScoreBox>
                          <TermScore>
                            {student.religiousTerm1 || '0'}/{student.religiousTerm2 || '0'}/{student.religiousTerm3 || '0'}
                          </TermScore>
                          <AvgChip 
                            label={calculateAverage(student.religiousTerm1, student.religiousTerm2, student.religiousTerm3)} 
                            size="small" 
                            color="secondary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </ScoreBox>
                      </StyledTableCell>
                      
                      {/* Year */}
                      <StyledTableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {student.year || "N/A"}
                        </Typography>
                      </StyledTableCell>
                      
                      {/* Registration Status */}
                      <StyledTableCell>
                        <Chip 
                          label={getRegStatusLabel(getRegStatusValue(student))}
                          color={getRegStatusValue(student) === 2 ? "warning" : getRegStatusValue(student) === 3 ? "error" : "success"}
                          size="small"
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: "1px solid #e5e7eb", justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={() => handlePrint()} 
            variant="contained" 
            startIcon={<Print />}
            disabled={loading || students.length === 0}
            sx={{ bgcolor: "#243b5a", '&:hover': { bgcolor: "#1a2c42" } }}
          >
            Print Registry
          </Button>
          <Button 
            onClick={handleDownloadStudentPDF} 
            variant="outlined" 
            startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
            disabled={loading || students.length === 0 || generatingPDF}
            sx={{ 
              color: '#d32f2f', 
              borderColor: '#d32f2f',
              '&:hover': { borderColor: '#b71c1c', bgcolor: 'rgba(211, 47, 47, 0.04)' } 
            }}
          >
            {generatingPDF ? 'Generating PDF...' : 'Download PDF'}
          </Button>
        </Box>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: "#F44336" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Main Component
// ============================================
const SchoolStudentDashboard: React.FC = () => {
  const [lgas, setLgas] = useState<LGA[]>([]);
  const [selectedLGA, setSelectedLGA] = useState<string>("");
  const [selectedRegStatus, setSelectedRegStatus] = useState<'all' | 1 | 2 | 3>('all');
  const [schools, setSchools] = useState<School[]>([]);
  const [filteredSchools, setFilteredSchools] = useState<School[]>([]);
  const [schoolStatusFilter, setSchoolStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Loading states
  const [loadingLGAs, setLoadingLGAs] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [schoolStudents, setSchoolStudents] = useState<Student[]>([]);
  
  // School list modal state
  const [schoolListModalOpen, setSchoolListModalOpen] = useState(false);
  const [schoolListTitle, setSchoolListTitle] = useState("");
  const [schoolListData, setSchoolListData] = useState<School[]>([]);
  const [loadingSchoolList, setLoadingSchoolList] = useState(false);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState<{ type: 'success' | 'error' | '', text: string }>({ type: '', text: '' });
  
  // Navigation state
  const [activeMenu, setActiveMenu] = useState<string>("school_dashboard");
  
  // Tab state
const [currentTab, setCurrentTab] = useState<number>(0); // 0 for School Directory, 1 for Subject Overview
  const [subjectStats, setSubjectStats] = useState<SubjectStat[]>([]);
  const [subjectSummary, setSubjectSummary] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState<boolean>(false);
  
  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  useEffect(() => {
    const path = location.pathname;
    if (path === "/student_list" || path === "/admin_students") {
      setActiveMenu("admin_students");
      setCurrentTab(0);
    }
  }, [location.pathname]);

  // Get user data from utils
  const user = getUser();
  
  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  // Load LGAs on component mount
  useEffect(() => {
    fetchLGAs();
  }, []);

  // Filter schools when search term or status changes
  useEffect(() => {
    let result = schools;
    
    // Search Term Filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      result = result.filter(school => 
        school.schoolName.toLowerCase().includes(term) ||
        (school.schoolId && school.schoolId.toLowerCase().includes(term)) ||
        (school.schoolCode && String(school.schoolCode).toLowerCase().includes(term))
      );
    }
    
    // School Status Filter (Active/Inactive)
    if (schoolStatusFilter === 'active') {
      result = result.filter(school => (Number(school.totalStudents) || 0) > 0);
    } else if (schoolStatusFilter === 'inactive') {
      result = result.filter(school => (Number(school.totalStudents) || 0) === 0);
    }
    
    setFilteredSchools(result);
  }, [searchTerm, schools, schoolStatusFilter]);

  const fetchLGAs = async () => {
    setLoadingLGAs(true);
    try {
      const data = await apiService.getLGAs();
      setLgas(data || []);
      setMessage({ type: "success", text: "LGAs loaded successfully" });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error("Error fetching LGAs:", error);
      setMessage({ type: "error", text: "Error fetching LGAs" });
    } finally {
      setLoadingLGAs(false);
    }
  };

  const handleLGAChange = async (lgaCode: string, status: 'all' | 1 | 2 | 3 = selectedRegStatus) => {
    setSelectedLGA(lgaCode);
    setSelectedRegStatus(status);
    
    if (!lgaCode) {
      setSchools([]);
      setFilteredSchools([]);
      return;
    }

    setLoadingSchools(true);
    try {
      const data = await apiService.getSchoolsByLGA(lgaCode, status);
      const safeData = data || [];
      setSchools(safeData);
      console.log("Schools data:", safeData);
      setFilteredSchools(safeData);
      
      setMessage({ type: "success", text: "Schools loaded successfully" });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error("Error fetching schools:", error);
      setMessage({ type: "error", text: "Error fetching schools" });
    } finally {
      setLoadingSchools(false);
    }
  };

  // Fetch subject stats when LGA changes
  useEffect(() => {
    const fetchSubjectStats = async () => {
      if (!selectedLGA) {
        setSubjectStats([]);
        setSubjectSummary(null);
        return;
      }
      
      setLoadingStats(true);
      try {
        const statsResponse = await apiService.getSubjectStatsByLGA(selectedLGA);
        if (statsResponse.status && statsResponse.data) {
          setSubjectStats(statsResponse.data);
          setSubjectSummary(statsResponse.summary);
          console.log("Subject stats loaded:", statsResponse.data);
          console.log("Subject summary:", statsResponse.summary);
        } else {
          setSubjectStats([]);
          setSubjectSummary(null);
        }
      } catch (error) {
        console.error("Error fetching subject stats:", error);
        setSubjectStats([]);
        setSubjectSummary(null);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchSubjectStats();
  }, [selectedLGA]);

  const handleRegStatusChange = async (status: 'all' | 1 | 2 | 3) => {
    setSelectedRegStatus(status);
    if (selectedLGA) {
      await handleLGAChange(selectedLGA, status);
    }
  };

  const handleViewSchool = async (school: School) => {
    setSelectedSchool(school);
    setModalOpen(true);
    setLoadingStudents(true);
    
    try {
      // Use schoolCode if available, otherwise use schoolId
      const schoolCode = school.schoolCode || school.schoolId;
      const data = await apiService.getStudentsBySchool(String(schoolCode), selectedLGA);
      
      console.log("Student data received:", data);
      
      // Set the students data - handle different response structures
      if (data && data.status && data.data) {
        setSchoolStudents(data.data);
      } else if (Array.isArray(data)) {
        setSchoolStudents(data);
      } else {
        setSchoolStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setSchoolStudents([]);
      setMessage({ type: "error", text: "Error loading students" });
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleViewPhotobook = (school: School) => {
    const schoolCode = school.schoolCode || school.schoolId;
    const lgaCode = selectedLGA;
    const schoolName = school.schoolName;
    
    history.push(`/photobook?schoolCode=${schoolCode}&lgaCode=${lgaCode}&schoolName=${encodeURIComponent(schoolName)}`);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSchool(null);
    setSchoolStudents([]);
  };

  const handleSchoolTypeClick = (type: 'public' | 'private') => {
    const filtered = schools.filter(school => 
      school.schoolType?.toLowerCase() === type
    );
    setSchoolListTitle(`${type === 'public' ? 'Public' : 'Private'} Schools`);
    setSchoolListData(filtered);
    setSchoolListModalOpen(true);
  };

  const handleViewSchoolFromList = async (school: School) => {
    await handleViewSchool(school);
  };

  const handleMenuClick = (menu: string) => {
    history.push("/" + menu);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  const handleRefresh = () => {
    fetchLGAs();
    if (selectedLGA) {
      handleLGAChange(selectedLGA, selectedRegStatus);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSchoolStatusFilter("all");
    setSelectedRegStatus("all");
  };

  const handleDownloadPDF = async () => {
    if (!selectedLGA || filteredSchools.length === 0) {
      setMessage({ type: "error", text: "No data to download" });
      return;
    }

    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      
      let yPosition = 20;

      // Find the selected LGA name
      const selectedLGAName = lgas.find(l => l.lgaCode === selectedLGA)?.name || lgas.find(l => l.lgaCode === selectedLGA)?.lgaName || selectedLGA;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - 10) {
          pdf.addPage();
          yPosition = 20;
        }
      };

      // Add header
      pdf.setFillColor(36, 59, 90); // Primary color
      pdf.rect(0, 0, pageWidth, 25, "F");
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.text("Ministry of Primary Education", margin, 12);
      pdf.setFontSize(11);
      pdf.text("School Directory Report", margin, 20);
      pdf.setDrawColor(200, 200, 200);
      pdf.line(0, 26, pageWidth, 26);

      yPosition = 35;

      // Add LGA info
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont(undefined, "bold");
      pdf.text(`Local Government Area: ${selectedLGAName}`, margin, yPosition);
      yPosition += 8;
      pdf.setFont(undefined, "normal");
      pdf.setFontSize(10);
      pdf.text(`Report Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, margin, yPosition);
      yPosition += 7;
      pdf.text(`Total Schools: ${String(totalSchools)}`, margin, yPosition);
      yPosition += 12;

      // Add stats section
      pdf.setFontSize(11);
      pdf.setFont(undefined, "bold");
      pdf.text("Summary Statistics", margin, yPosition);
      yPosition += 10;

      pdf.setFont(undefined, "normal");
      pdf.setFontSize(10);

      // Stats boxes with better layout
      const statsBoxWidth = (contentWidth - 5) / 2;
      const boxHeight = 26;
      const statsBoxX1 = margin;
      const statsBoxX2 = margin + statsBoxWidth + 5;

      // Public Schools box
      pdf.setDrawColor(200, 220, 255);
      pdf.setFillColor(243, 250, 255);
      pdf.rect(statsBoxX1, yPosition, statsBoxWidth, boxHeight, "FD");
      pdf.setFont(undefined, "bold");
      pdf.setFontSize(11);
      pdf.text("Public Schools: " + String(totalPublicSchools), statsBoxX1 + 4, yPosition + 7);
      pdf.setFont(undefined, "normal");
      pdf.setFontSize(10);
      pdf.text("Total Students: " + String(formatNumber(totalPublicStudents)), statsBoxX1 + 4, yPosition + 16);

      // Private Schools box
      pdf.setDrawColor(200, 220, 200);
      pdf.setFillColor(243, 250, 243);
      pdf.rect(statsBoxX2, yPosition, statsBoxWidth, boxHeight, "FD");
      pdf.setFont(undefined, "bold");
      pdf.setFontSize(11);
      pdf.text("Private Schools: " + String(totalPrivateSchools), statsBoxX2 + 4, yPosition + 7);
      pdf.setFont(undefined, "normal");
      pdf.setFontSize(10);
      pdf.text("Total Students: " + String(formatNumber(totalPrivateStudents)), statsBoxX2 + 4, yPosition + 16);

      yPosition += boxHeight + 15;

      // Add schools table
      checkNewPage(25);
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, "bold");
      pdf.text("Schools List", margin, yPosition);
      yPosition += 10;

      // Table header with white background and black text for better visibility
      pdf.setDrawColor(36, 59, 90);
      pdf.setFontSize(9);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(0, 0, 0);

      const colWidth = {
        sn: 10,
        school: contentWidth * 0.65,
        id: contentWidth * 0.15,
        students: contentWidth * 0.10,
      };

      const tableHeaderY = yPosition;
      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin, tableHeaderY, colWidth.sn, 8, "FD");
      pdf.text("S/N", margin + 1, tableHeaderY + 5);

      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin + colWidth.sn, tableHeaderY, colWidth.school, 8, "FD");
      pdf.text("School Name", margin + colWidth.sn + 2, tableHeaderY + 5);

      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin + colWidth.sn + colWidth.school, tableHeaderY, colWidth.id, 8, "FD");
      pdf.text("School ID", margin + colWidth.sn + colWidth.school + 2, tableHeaderY + 5);

      pdf.setFillColor(255, 255, 255);
      pdf.rect(
        margin + colWidth.sn + colWidth.school + colWidth.id,
        tableHeaderY,
        colWidth.students,
        8,
        "FD"
      );
      pdf.text("Total Stu.", margin + colWidth.sn + colWidth.school + colWidth.id + 2, tableHeaderY + 5);

      yPosition += 12;

      // Table rows
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(0, 0, 0);
      let rowNumber = 1;

      for (const school of filteredSchools) {
        checkNewPage(7);

        // Alternate row colors
        if (rowNumber % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(margin, yPosition - 4, contentWidth, 6, "F");
        }

        pdf.setFontSize(9);
        pdf.text(String(rowNumber), margin + 2, yPosition);
        
        // School name - truncate if too long
        const schoolNameDisplay = school.schoolName.substring(0, 60);
        pdf.text(schoolNameDisplay, margin + colWidth.sn + 2, yPosition);
        
        // School ID
        const schoolIdDisplay = String(school.schoolCode || school.schoolId || "N/A");
        pdf.text(schoolIdDisplay, margin + colWidth.sn + colWidth.school + 2, yPosition);
        
        // Total Students
        pdf.text(String(formatNumber(school.totalStudents)), margin + colWidth.sn + colWidth.school + colWidth.id + 2, yPosition);

        pdf.setDrawColor(220, 220, 220);
        pdf.line(margin, yPosition + 1, pageWidth - margin, yPosition + 1);

        yPosition += 6;
        rowNumber++;
      }

      // Add footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      const footerY = pageHeight - 10;
      pdf.text(
        "Generated on " + new Date().toLocaleDateString() + " | Ministry of Primary Education",
        pageWidth / 2,
        footerY,
        { align: "center" }
      );

      // Save PDF
      pdf.save(`Schools_${selectedLGAName}_${new Date().getTime()}.pdf`);
      setMessage({ type: "success", text: "PDF downloaded successfully" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setMessage({ type: "error", text: `Error generating PDF: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleDownloadSubjectPDF = async () => {
    if (!selectedLGA || subjectStats.length === 0) {
      setMessage({ type: "error", text: "No subject data to download" });
      return;
    }

    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - 2 * margin;
      
      let yPosition = 20;

      // Find the selected LGA name
      const selectedLGAName = lgas.find(l => l.lgaCode === selectedLGA)?.name || lgas.find(l => l.lgaCode === selectedLGA)?.lgaName || selectedLGA;

      // Add header
      pdf.setFillColor(36, 59, 90); // Primary color
      pdf.rect(0, 0, pageWidth, 20, "F");
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.text("Ministry of Primary Education", margin, 12);
      pdf.setFontSize(10);
      pdf.text("Subject Registration Overview - " + selectedLGAName, margin, 18);
      pdf.setDrawColor(200, 200, 200);
      pdf.line(0, 21, pageWidth, 21);

      yPosition = 30;

      // Add report info
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      pdf.text(`Report Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, margin, yPosition);
      pdf.text(`Total Schools: ${subjectStats.length}`, pageWidth - margin - 40, yPosition);
      yPosition += 10;

      // Table headers
      const colWidths = {
        sn: 10,
        school: 110,
        english: 18,
        arithmetic: 18,
        general: 18,
        crs: 18,
        irs: 18,
        total: 18
      };

      pdf.setFontSize(8);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(0, 0, 0);

      // Header row
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition, contentWidth, 6, "F");
      pdf.text("S/N", margin + 1, yPosition + 4);
      pdf.text("SCHOOL NAME", margin + colWidths.sn + 1, yPosition + 4);
      pdf.text("ENGLISH", margin + colWidths.sn + colWidths.school + colWidths.english - 15, yPosition + 4, { align: "center" });
      pdf.text("ARITHMETIC", margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic - 15, yPosition + 4, { align: "center" });
      pdf.text("GENERAL", margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general - 15, yPosition + 4, { align: "center" });
      pdf.text("CRS", margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs - 15, yPosition + 4, { align: "center" });
      pdf.text("IRS", margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs + colWidths.irs - 15, yPosition + 4, { align: "center" });
      pdf.text("TOTAL", margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs + colWidths.irs + colWidths.total - 15, yPosition + 4, { align: "center" });

      yPosition += 6;

      // Data rows
      pdf.setFont(undefined, "normal");
      subjectStats.forEach((stat, index) => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.text(String(index + 1), margin + 1, yPosition + 4);
        pdf.text(stat.schoolName, margin + colWidths.sn + 1, yPosition + 4);
        pdf.text(String(stat.regEnglish || 0), margin + colWidths.sn + colWidths.school + colWidths.english / 2, yPosition + 4, { align: "center" });
        pdf.text(String(stat.regArithmetic || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic / 2, yPosition + 4, { align: "center" });
        pdf.text(String(stat.regGeneral || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general / 2, yPosition + 4, { align: "center" });
        pdf.text(String(stat.regCRS || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs / 2, yPosition + 4, { align: "center" });
        pdf.text(String(stat.regIRS || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs + colWidths.irs / 2, yPosition + 4, { align: "center" });
        pdf.text(String(stat.totalStudents || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs + colWidths.irs + colWidths.total / 2, yPosition + 4, { align: "center" });

        yPosition += 5;
      });

      // Total row
      if (subjectSummary) {
        if (yPosition > pageHeight - 15) {
          pdf.addPage();
          yPosition = 20;
        }

        pdf.setFont(undefined, "bold");
        pdf.setFillColor(36, 59, 90);
        pdf.setTextColor(255, 255, 255);
        pdf.rect(margin, yPosition, contentWidth, 6, "F");
        pdf.text("TOTAL", margin + 1, yPosition + 4);
        pdf.text(`All Schools (${subjectStats.length})`, margin + colWidths.sn + 1, yPosition + 4);
        pdf.text(formatNumber(subjectSummary.regEnglish || 0), margin + colWidths.sn + colWidths.school + colWidths.english / 2, yPosition + 4, { align: "center" });
        pdf.text(formatNumber(subjectSummary.regArithmetic || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic / 2, yPosition + 4, { align: "center" });
        pdf.text(formatNumber(subjectSummary.regGeneral || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general / 2, yPosition + 4, { align: "center" });
        pdf.text(formatNumber(subjectSummary.regCRS || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs / 2, yPosition + 4, { align: "center" });
        pdf.text(formatNumber(subjectSummary.regIRS || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs + colWidths.irs / 2, yPosition + 4, { align: "center" });
        pdf.text(formatNumber(subjectSummary.totalStudents || 0), margin + colWidths.sn + colWidths.school + colWidths.english + colWidths.arithmetic + colWidths.general + colWidths.crs + colWidths.irs + colWidths.total / 2, yPosition + 4, { align: "center" });
      }

      // Add footer
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      const footerY = pageHeight - 8;
      pdf.text(
        "Generated on " + new Date().toLocaleDateString() + " | Ministry of Primary Education",
        pageWidth / 2,
        footerY,
        { align: "center" }
      );

      // Save PDF
      pdf.save(`Subject_Overview_${selectedLGAName}_${new Date().getTime()}.pdf`);
      setMessage({ type: "success", text: "Subject overview PDF downloaded successfully" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error generating subject PDF:", error);
      setMessage({ type: "error", text: `Error generating PDF: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  // Calculate totals
  const totalSchools = schools.length;
  const totalStudents = schools.reduce((sum, school) => sum + (Number(school.totalStudents) || 0), 0);
  const totalPublicSchools = schools.filter(s => s.schoolType?.toLowerCase() === 'public').length;
  const totalPrivateSchools = schools.filter(s => s.schoolType?.toLowerCase() === 'private').length;
  
  // Calculate total students in public and private schools
  const totalPublicStudents = schools
    .filter(s => s.schoolType?.toLowerCase() === 'public')
    .reduce((sum, school) => sum + (Number(school.totalStudents) || 0), 0);
  
  const totalPrivateStudents = schools
    .filter(s => s.schoolType?.toLowerCase() === 'private')
    .reduce((sum, school) => sum + (Number(school.totalStudents) || 0), 0);

  // Format number helper
  const formatNumber = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '0';
    const num = Number(value);
    return isNaN(num) ? '0' : num.toLocaleString();
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching AdminDashboard */}
            <DashboardHeader>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      School Management Dashboard
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <SchoolInfoBox>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                    >
                      {userData.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                    </Typography>
                  </SchoolInfoBox>
                  <IconButton
                    onClick={handleRefresh}
                    title="Refresh Data"
                    sx={{
                      color: "#ffffff",
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Box>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              {/* Navigation Menu - Matching AdminDashboard */}
              <NavMenu>
                <NavButton
                  active={activeMenu === "admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={activeMenu === "lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={activeMenu === "add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={activeMenu === "admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
              </NavMenu>
            </DashboardHeader>


            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a237e" }}>
                  School Directory
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Select a Local Government Area to view schools and student statistics
                </Typography>
              </Box>

              {/* Message Alert */}
              {message.text && (
                <Alert 
                  severity={message.type === 'success' ? 'success' : 'error'} 
                  sx={{ mb: 3 }}
                  onClose={() => setMessage({ type: '', text: '' })}
                >
                  {message.text}
                </Alert>
              )}

              {/* LGA Selection Card */}
              <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Select Local Government Area</InputLabel>
                      <Select
                        value={selectedLGA}
                        label="Select Local Government Area"
                        onChange={(e) => handleLGAChange(e.target.value)}
                        disabled={loadingLGAs}
                        startAdornment={
                          <LocationIcon sx={{ mr: 1, color: "action.active" }} />
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {lgas && lgas.map((lga) => (
                          <MenuItem key={lga.lgaCode} value={lga.lgaCode}>
                            {lga.name || lga.lgaName} - ({lga.lgaCode})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {selectedLGA && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Card sx={{ flex: 1, bgcolor: "#e3f2fd" }}>
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Schools
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                              {totalSchools}
                            </Typography>
                          </CardContent>
                        </Card>
                        <Card sx={{ flex: 1, bgcolor: "#e8f5e8" }}>
                          <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Total Students
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                              {formatNumber(totalStudents)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* School Type Summary Cards - UPDATED with total students */}
              {selectedLGA && (
                <StatsGrid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6}>
                    <StatCard onClick={() => handleSchoolTypeClick('public')}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Business sx={{ fontSize: 40, color: "#3b82f6" }} />
                        <ViewButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSchoolTypeClick('public');
                          }}
                        >
                          View All
                        </ViewButton>
                      </Box>
                      <StatLabel>Public Schools</StatLabel>
                      <StatValue>{totalPublicSchools}</StatValue>
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: "#059669" }}>
                        Total Students: {formatNumber(totalPublicStudents)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        {totalPublicSchools > 0 ? `${Math.round((totalPublicSchools / totalSchools) * 100)}% of total schools` : 'No public schools'}
                      </Typography>
                    </StatCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StatCard onClick={() => handleSchoolTypeClick('private')}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Home sx={{ fontSize: 40, color: "#10b981" }} />
                        <ViewButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSchoolTypeClick('private');
                          }}
                        >
                          View All
                        </ViewButton>
                      </Box>
                      <StatLabel>Private Schools</StatLabel>
                      <StatValue>{totalPrivateSchools}</StatValue>
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: "#059669" }}>
                        Total Students: {formatNumber(totalPrivateStudents)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        {totalPrivateSchools > 0 ? `${Math.round((totalPrivateSchools / totalSchools) * 100)}% of total schools` : 'No private schools'}
                      </Typography>
                    </StatCard>
                  </Grid>
                </StatsGrid>
              )}

              {/* Schools List Section with Tabs */}
              {selectedLGA && (
                <>
                  {/* Tab Navigation */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 1 }}>
                      <TabButton 
                        onClick={() => setCurrentTab(0)}
                        active={currentTab === 0}
                      >
                        School Directory
                      </TabButton>
                      <TabButton 
                        onClick={() => setCurrentTab(1)}
                        active={currentTab === 1}
                        startIcon={<Assessment />}
                      >
                        Subject Overview
                      </TabButton>
                    </Stack>
                  </Box>

                  {/* TAB 0: School Directory Table */}
                  {currentTab === 0 && (
                    <Box>
                      {/* Search Bar - Matching AdminDashboard style */}
                      <FilterBar>
                        <SearchField
                          size="small"
                          placeholder="Search schools by name or ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <ActionButton
                          size="small"
                          onClick={handleClearFilters}
                          startIcon={<FilterList />}
                        >
                          Clear Filters
                        </ActionButton>

                        <FilterSelect size="small">
                          <InputLabel id="reg-status-filter-label">Reg. Status</InputLabel>
                          <Select
                            labelId="reg-status-filter-label"
                            value={selectedRegStatus}
                            label="Reg. Status"
                            size="small"
                            onChange={(e) => handleRegStatusChange(e.target.value as 'all' | 1 | 2 | 3)}
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value={1}>Normal</MenuItem>
                            <MenuItem value={2}>Late</MenuItem>
                            <MenuItem value={3}>Pos Late</MenuItem>
                          </Select>
                        </FilterSelect>

                        <FilterSelect size="small">
                          <InputLabel id="school-status-filter-label">Status</InputLabel>
                          <Select
                            labelId="school-status-filter-label"
                            value={schoolStatusFilter}
                            label="Status"
                            size="small"
                            onChange={(e) => setSchoolStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">In-active</MenuItem>
                          </Select>
                        </FilterSelect>

                        <ActionButton
                          size="small"
                          onClick={handleDownloadPDF}
                          disabled={!selectedLGA || filteredSchools.length === 0}
                          startIcon={<GetApp />}
                          title="Download schools data as PDF"
                        >
                          Download PDF
                        </ActionButton>

                        <IconActionButton 
                          size="small" 
                          onClick={handleRefresh}
                        >
                          <Refresh />
                        </IconActionButton>
                      </FilterBar>

                      {/* Schools Grid/Table */}
                      {loadingSchools ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                          <CircularProgress />
                          <Typography sx={{ ml: 2 }}>Loading schools...</Typography>
                        </Box>
                      ) : filteredSchools.length === 0 ? (
                        <Alert severity="info">
                          No schools found in this Local Government Area.
                        </Alert>
                      ) : (
                        <>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Showing {filteredSchools.length} of {schools.length} schools
                            </Typography>
                          </Box>

                          <StyledTableContainer component={Paper}>
                            <Table>
                              <StyledTableHead>
                                <TableRow>
                                  <TableCell><b>S/N</b></TableCell>
                                  <TableCell><b>SCHOOL NAME</b></TableCell>
                                  <TableCell><b>SCHOOL ID</b></TableCell>
                                  <TableCell align="center"><b>TOTAL STUDENTS</b></TableCell>
                                  <TableCell align="center"><b>STATUS</b></TableCell>
                                  <TableCell align="center"><b>ACTION</b></TableCell>
                                  <TableCell align="center"><b>PHOTOBOOK</b></TableCell>
                                </TableRow>
                              </StyledTableHead>
                              <TableBody>
                                {filteredSchools.map((school, index) => (
                                  <StyledTableRow key={school.schoolId || index}>
                                    <StyledTableCell>{index + 1}</StyledTableCell>
                                    <StyledTableCell>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: "#243b5a" }}>
                                          <School sx={{ fontSize: 16 }} />
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {school.schoolName}
                                        </Typography>
                                      </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                      <Chip
                                        label={school.schoolCode || school.schoolId || 'N/A'}
                                        size="small"
                                        sx={{ bgcolor: "#e3f2fd", color: "#243b5a", fontWeight: 600 }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {formatNumber(school.totalStudents)}
                                      </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Chip
                                        label={school.totalStudents > 0 ? "Active" : "Inactive"}
                                        size="small"
                                        sx={{
                                          bgcolor: school.totalStudents > 0 ? "#10b981" : "#9ca3af",
                                          color: "white",
                                          minWidth: 60,
                                        }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <ViewButton
                                        size="small"
                                        onClick={() => handleViewSchool(school)}
                                        startIcon={<Visibility />}
                                      >
                                        View
                                      </ViewButton>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleViewPhotobook(school)}
                                        sx={{ color: "#f59e0b" }}
                                        title="View Photobook"
                                      >
                                        <PhotoLibrary />
                                      </IconButton>
                                    </StyledTableCell>
                                  </StyledTableRow>
                                ))}
                              </TableBody>
                            </Table>
                            
                            {/* Pagination Info */}
                            <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                              <Typography variant="body2" color="text.secondary">
                                Showing 1 to {filteredSchools.length} of {schools.length} schools
                              </Typography>
                            </Box>
                          </StyledTableContainer>
                        </>
                      )}
                    </Box>
                  )}

                  {/* TAB 1: Subject Overview Table */}
                  {currentTab === 1 && (
                    <Box>
                      {loadingStats ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                          <CircularProgress />
                          <Typography sx={{ ml: 2 }}>Loading subject statistics...</Typography>
                        </Box>
                      ) : subjectStats.length === 0 ? (
                        <Alert severity="info">
                          No subject statistics available for this Local Government Area.
                        </Alert>
                      ) : (
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Showing {subjectStats.length} schools with subject performance data
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<PictureAsPdf />}
                              onClick={handleDownloadSubjectPDF}
                              sx={{ bgcolor: "#243b5a", "&:hover": { bgcolor: "#1e2f4a" } }}
                            >
                              Download PDF
                            </Button>
                          </Box>

                          <StyledTableContainer component={Paper} sx={{ mb: 4 }}>
                            <Table stickyHeader size="small">
                              <StyledTableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>S/N</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>SCHOOL NAME</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>ENGLISH</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>ARITHMETIC</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>GENERAL</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>CRS (Christian)</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>IRS (Islam)</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>TOTAL STUDENTS</TableCell>
                                </TableRow>
                              </StyledTableHead>
                              <TableBody>
                                {subjectStats.map((stat, index) => (
                                  <StyledTableRow key={index}>
                                    <StyledTableCell>{index + 1}</StyledTableCell>
                                    <StyledTableCell sx={{ fontWeight: 600 }}>{stat.schoolName}</StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Chip 
                                        label={stat.regEnglish || 0} 
                                        size="small" 
                                        sx={{ bgcolor: "#e3f2fd", color: "#1976d2", fontWeight: 700, minWidth: 50 }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Chip 
                                        label={stat.regArithmetic || 0} 
                                        size="small" 
                                        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, minWidth: 50 }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Chip 
                                        label={stat.regGeneral || 0} 
                                        size="small" 
                                        sx={{ bgcolor: "#fff3e0", color: "#ef6c00", fontWeight: 700, minWidth: 50 }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Chip 
                                        label={stat.regCRS || 0} 
                                        size="small" 
                                        sx={{ bgcolor: "#f3e5f5", color: "#7b1fa2", fontWeight: 700, minWidth: 50 }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Chip 
                                        label={stat.regIRS || 0} 
                                        size="small" 
                                        sx={{ bgcolor: "#e0f2f1", color: "#00695c", fontWeight: 700, minWidth: 50 }}
                                      />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                        {stat.totalStudents || 0}
                                      </Typography>
                                    </StyledTableCell>
                                  </StyledTableRow>
                                ))}
                                {/* Total Row */}
                                {subjectSummary && (
                                  <StyledTableRow sx={{ bgcolor: "#f8f9fa", borderTop: "2px solid #243b5a" }}>
                                    <StyledTableCell sx={{ fontWeight: 800, color: "#243b5a" }}>TOTAL</StyledTableCell>
                                    <StyledTableCell sx={{ fontWeight: 800, color: "#243b5a" }}>
                                      All Schools ({subjectStats.length})
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#1976d2" }}>
                                        {formatNumber(subjectSummary.regEnglish || 0)}
                                      </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#2e7d32" }}>
                                        {formatNumber(subjectSummary.regArithmetic || 0)}
                                      </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#ef6c00" }}>
                                        {formatNumber(subjectSummary.regGeneral || 0)}
                                      </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#7b1fa2" }}>
                                        {formatNumber(subjectSummary.regCRS || 0)}
                                      </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#00695c" }}>
                                        {formatNumber(subjectSummary.regIRS || 0)}
                                      </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                      <Typography variant="body2" sx={{ fontWeight: 800, color: "#243b5a" }}>
                                        {formatNumber(subjectSummary.totalStudents || 0)}
                                      </Typography>
                                    </StyledTableCell>
                                  </StyledTableRow>
                                )}
                              </TableBody>
                            </Table>
                          </StyledTableContainer>

                          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                            <Typography variant="body2" color="text.secondary">
                              Total Schools Analyzed: {subjectStats.length}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}

              {/* No LGA Selected Message */}
              {!selectedLGA && (
                <Paper sx={{ p: 6, textAlign: "center", bgcolor: "#fafafa" }}>
                  <LocationIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a Local Government Area to view schools
                  </Typography>
                </Paper>
              )}
            </Container>

            {/* FOOTER - Exactly matching AdminDashboard */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", maxWidth: 900, mx: "auto", mb: 2 }}
                >
                  OFFICIAL NOTICE: This is an official portal of the Delta State Government, Ministry of Primary Education. Unauthorized access, misuse, or tampering with this system is prohibited and may be subject to legal action. All information provided is for official purposes only.
                </Typography>

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>
          </Box>

          {/* Student Details Modal */}
          <StudentModal
            open={modalOpen}
            onClose={handleCloseModal}
            school={selectedSchool}
            students={schoolStudents}
            loading={loadingStudents}
          />

          {/* School List Modal */}
          <SchoolListModal
            open={schoolListModalOpen}
            onClose={() => setSchoolListModalOpen(false)}
            title={schoolListTitle}
            schools={schoolListData}
            loading={loadingSchoolList}
            onViewSchool={handleViewSchoolFromList}
          />
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default SchoolStudentDashboard;